import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Froala from '@/components/shared/Froala';
import cn from '@/utils/cn';
import compactSelectStyles from '../../shared/compactSelectStyles';
import RoutedSPADialog from '../../shared/RoutedSPADialog';
import SPACheckbox from '../../shared/SPACheckbox';
import SPADialog from '../../shared/SPADialog';
import SPAInput from '../../shared/SPAInput';
import SPASelect from '../../shared/SPASelect';
import { useSPAToast } from '../../shared/SPAToast';
import ResourceIconTile from './ResourceIconTile';
import { Field, hintClass } from '../../shared/formPanel';
import {
  ProgramResource, ResourceOwner, ResourcePayload, ResourceTemplate, resourceErrorMessage, useProgramResources,
  useResourceTemplates, useSaveResource,
} from './useProgramResources';

const AUTO_GENERATED = 'Auto-generated';

// Relative to the route tree, not the URL: both dialogs are one route under the resources section.
const RETURN_PATH = '..';

// Program resources take the defaults the model applies (ModelResource#set_defaults) unless the
// category overrides them, as the legacy form's disabled Type and Audience selects did.
const DEFAULT_INPUT_TYPE = 'HTML';
const DEFAULT_AUDIENCE = 'Teacher';

interface TemplateOption {
  label: string;
  template: ResourceTemplate;
  value: number;
}

const FROALA_CONFIG = {
  toolbarButtons: [
    'bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript',
    'fontSize', 'textColor', 'backgroundColor', 'inlineStyle', 'paragraphFormat',
    'align', 'formatOL', 'formatUL', 'outdent', 'indent', 'quote', 'insertHR', '-',
    'insertLink', 'insertTable', 'undo', 'redo', 'clearFormatting', 'selectAll', 'html',
  ],
};

interface FormState {
  html: string;
  published: boolean;
  templateId: number | null;
  title: string;
  url: string;
}

const initialState = (resource?: ProgramResource): FormState => ({
  html: resource?.html ?? '',
  published: resource?.published ?? true,
  templateId: resource?.model_resource_template_id ?? null,
  title: resource?.title ?? '',
  url: resource?.url ?? '',
});

interface ResourceFormProps {
  owner: ResourceOwner;
  resource?: ProgramResource;
  templates: ResourceTemplate[];
}

const ResourceForm = ({ owner, resource, templates }: ResourceFormProps) => {
  const navigate = useNavigate();
  const spaToast = useSPAToast();
  const save = useSaveResource(owner);
  const [form, setForm] = useState<FormState>(() => initialState(resource));
  const options = useMemo<TemplateOption[]>(
    () => templates.map(template => ({ label: template.name, template, value: template.id })),
    [templates],
  );
  const selected = options.find(option => option.value === form.templateId) ?? null;
  const template = selected?.template;
  const inputType = template?.input_type_override || resource?.input_type || DEFAULT_INPUT_TYPE;
  const audience = template?.audience_override || resource?.audience || DEFAULT_AUDIENCE;
  const autoGenerated = template?.auto_generated ?? false;
  const titleLocked = Boolean(template?.title_override);

  // A category's overrides fill in as soon as it is picked; leaving an auto-generated category
  // clears the placeholder it left behind.
  const pickTemplate = (option: TemplateOption | null) => {
    const next = option?.template;
    setForm(current => ({
      ...current,
      html: !next?.auto_generated && current.html === AUTO_GENERATED ? '' : current.html,
      templateId: option?.value ?? null,
      title: next?.title_override ?? current.title,
      url: !next?.auto_generated && current.url === AUTO_GENERATED ? '' : current.url,
    }));
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.templateId) return;

    // Auto-generated categories have no content of their own; the others send one field or the other.
    let content: Partial<ResourcePayload> = {};
    if (!autoGenerated) content = inputType === 'Link' ? { url: form.url } : { html: form.html };

    const payload: Partial<ResourcePayload> = {
      audience,
      input_type: inputType,
      model_resource_template_id: form.templateId,
      program_id: owner.programId,
      published: form.published,
      title: form.title,
      ...content,
    };

    save.mutate({ id: resource?.id, resource: payload }, {
      onSuccess: (saved) => {
        spaToast.success(`${resource ? 'Saved' : 'Added'} “${saved.title}”.`);
        navigate(RETURN_PATH);
      },
    });
  };

  return (
    <form autoComplete="off" className="tw-flex tw-flex-1 tw-flex-col" onSubmit={submit}>
      <SPADialog.Body className="tw-flex tw-flex-col tw-gap-4">
        <Field required htmlFor="resource_template" label="Category">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-min-w-0 tw-flex-1">
              <SPASelect<TemplateOption, false>
                inputId="resource_template"
                options={options}
                placeholder="Select a category"
                styles={compactSelectStyles}
                value={selected}
                onChange={pickTemplate}
              />
            </div>
            {template?.icon && <ResourceIconTile icon={template.icon} size="md" />}
          </div>
        </Field>

        <Field
          required
          hint={titleLocked ? 'This category sets the title.' : undefined}
          htmlFor="resource_title"
          label="Title"
        >
          <SPAInput
            disabled={titleLocked}
            id="resource_title"
            value={form.title}
            onChange={({ target: { value } }) => setForm(current => ({ ...current, title: value }))}
          />
        </Field>

        <dl className="tw-m-0 tw-flex tw-gap-6 tw-text-sm">
          <div className="tw-flex tw-gap-1.5">
            <dt className="tw-font-bold tw-text-spa-heading">Type</dt>
            <dd className="tw-m-0 tw-text-spa-secondary">{inputType}</dd>
          </div>
          <div className="tw-flex tw-gap-1.5">
            <dt className="tw-font-bold tw-text-spa-heading">Audience</dt>
            <dd className="tw-m-0 tw-text-spa-secondary">{audience}</dd>
          </div>
        </dl>

        <SPACheckbox
          checked={form.published}
          onChange={({ target: { checked } }) => setForm(current => ({ ...current, published: checked }))}
        >
          Published
        </SPACheckbox>

        {autoGenerated && (
          <p className={cn(hintClass, 'tw-mt-0')}>
            This category’s content is generated automatically; there is nothing to write here.
          </p>
        )}
        {!autoGenerated && inputType === 'Link' && (
          <Field required htmlFor="resource_url" label="URL">
            <SPAInput
              id="resource_url"
              placeholder="https://"
              type="url"
              value={form.url}
              onChange={({ target: { value } }) => setForm(current => ({ ...current, url: value }))}
            />
          </Field>
        )}
        {!autoGenerated && inputType !== 'Link' && (
          <div>
            <span className="tw-mb-1.5 tw-block tw-text-sm tw-font-bold tw-text-spa-heading">Content</span>
            {/* The editor keeps its light styling; teachers read this content on light pages. */}
            <div className="tw-rounded tw-bg-white tw-text-neutral-900">
              <Froala
                customConfig={FROALA_CONFIG}
                modelId={resource?.id}
                modelType="ModelResource"
                name="resource_html"
                text={form.html}
                userType="Sysadmin"
                onChange={html => setForm(current => ({ ...current, html }))}
              />
            </div>
          </div>
        )}

        {save.isError && (
          <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">
            {resourceErrorMessage(save.error, 'The resource could not be saved. Try again.')}
          </p>
        )}
      </SPADialog.Body>
      <SPADialog.Footer>
        <SPADialog.Close asChild>
          <SPADialog.Button>Cancel</SPADialog.Button>
        </SPADialog.Close>
        <SPADialog.Button disabled={!form.templateId || save.isLoading} type="submit" variant="primary">
          {save.isLoading ? 'Saving…' : 'Save'}
        </SPADialog.Button>
      </SPADialog.Footer>
    </form>
  );
};

// AIDEV-NOTE: One routed dialog for both `new` and `:resourceId/edit`. The resource comes from the
// panel's list query, so a deep link into edit waits on that list rather than fetching one row.
const ResourceDialog = () => {
  const owner = useOutletContext<ResourceOwner>();
  const { resourceId } = useParams<{ resourceId?: string }>();
  const navigate = useNavigate();
  const resources = useProgramResources(owner);
  const templates = useResourceTemplates(owner.modelClass);
  const editing = resourceId !== undefined;
  const resource = editing ? resources.data?.find(row => row.id === Number(resourceId)) : undefined;
  const isLoading = templates.isLoading || (editing && resources.isLoading);
  const missing = editing && !resources.isLoading && !resource;

  useEffect(() => {
    if (missing) navigate(RETURN_PATH, { replace: true });
  }, [missing, navigate]);

  return (
    <RoutedSPADialog returnPath={RETURN_PATH}>
      <SPADialog.Content className="tw-overflow-visible" placement="top" size="lg">
        <SPADialog.Header>
          <SPADialog.Title>{editing ? 'Edit Resource' : 'Add Resource'}</SPADialog.Title>
          <SPADialog.Description className="tw-mt-1 tw-text-spa-secondary">
            {editing ? `Editing “${resource?.title ?? ''}”.` : 'A new resource on the resources page.'}
          </SPADialog.Description>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        {isLoading && (
          <SPADialog.Body>
            <p className="tw-m-0 tw-text-sm tw-text-spa-tertiary">Loading…</p>
          </SPADialog.Body>
        )}
        {!isLoading && templates.data && !missing && (
          <ResourceForm
            key={resource?.id ?? 'new'}
            owner={owner}
            resource={resource}
            templates={templates.data}
          />
        )}
      </SPADialog.Content>
    </RoutedSPADialog>
  );
};

export default ResourceDialog;
